<template>
  <div
    class="card shadow bb-card mb-2 armour-card"
    :class="{
      'upper-lower-card': isUpperOrLower,
      'limb-base-card': isLeftRightBase,
      'armour-empty': isEmpty,
    }"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <div class="card-body rounded m-1 mt-0">
      <!-- =========================================================== -->
      <!--   HEADER   -->
      <!-- =========================================================== -->
      <!-- --------- -->
      <!--   ROW 1   -->
      <!-- --------- -->
      <div class="row mt-1 mb-3">
        <!-- ------------------------ -->
        <!--   armour location name   -->
        <!-- ------------------------ -->
        <div
          class="col-7"
          id="location_name"
        >
          {{ al.armour_location_name }}
          <span
            v-if="equippedCount > 1"
            class="equipped-count text-left"
          >
            [{{ equippedCount }}]</span
          >
        </div>
        <!-- -------------------- -->
        <!--   body location HP   -->
        <!-- -------------------- -->
        <div
          v-if="bodyLocationHp !== ''"
          id="location_hp"
          class="col-5"
        >
          <span
            style="color: #737373"
            class="text-center"
            >Total HP
          </span>
          <span style="color: white">[{{ bodyLocationHp }}]</span>
        </div>
      </div>
      <!-- --------- -->
      <!--   ROW 2   -->
      <!-- --------- -->
      <!-- use smaller horizontal gutter between inputs -->
      <div class="row gx-1">
        <!-- -------------- -->
        <!--   armour name  -->
        <!-- -------------- -->
        <div class="col-7">
          <div class="form-group">
            <div class="form-floating">
              <input
                readonly
                class="form-control armour-name-input"
                :value="getHighestArmourName"
                id="armour_name"
                disabled
              />
              <label
                class="bb-float-label optional"
                for="armour_name"
                >Armour</label
              >
            </div>
          </div>
        </div>
        <!----------- -->
        <!--   AP/HP  -->
        <!-- -------- -->
        <div class="col-3">
          <div class="form-group">
            <div class="form-floating">
              <input
                readonly
                class="form-control text-center"
                :value="getHighestAPHP"
                id="armour_aphp"
                disabled
              />
              <label
                class="bb-float-label optional"
                for="armour_aphp"
                >AP/HP</label
              >
            </div>
          </div>
        </div>
        <!--------------- -->
        <!--   dex pen    -->
        <!-- ------------ -->
        <div class="col-2">
          <div class="form-group">
            <div class="form-floating">
              <input
                readonly
                class="form-control text-center"
                :value="formatDexPen(al.dex_pen)"
                id="armour_dex_pen"
                disabled
              />
              <label
                class="bb-float-label optional"
                for="armour_dex_pen"
                >Pen</label
              >
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- --------------------- -->
    <!-- overlay with all equipped armour for this location -->
    <div
      v-if="showOverlay && hasMultipleEquipped"
      class="armour-overlay"
    >
      <div class="armour-overlay-inner">
        <div class="armour-overlay-header">
          <span>Layer</span>
          <span>Armour</span>
          <span>AP/HP</span>
          <span>Dex Pen</span>
        </div>
        <div
          v-for="item in sortedEquipped"
          :key="item.id || item.layer"
          class="armour-overlay-row"
        >
          <span>{{ item.layer }}</span>
          <span>{{ item.armour_name }}</span>
          <span>{{ item.ap }} / {{ item.hp }}</span>
          <span>{{ formatDexPen(item.dex_pen) }}%</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ArmourCard',
  //========================================
  // component
  //-----------
  components: {},
  //========================================
  // props
  //-------
  props: {
    al: {
      // armour location
      type: Object,
      required: true,
    },
    bodyLocationHp: {
      type: [Number, String],
      default: '',
    },
  },
  //========================================
  // data
  //------
  data() {
    return {
      // lockArenaImg,
      showOverlay: false,
    }
  },
  //========================================
  // computed
  //----------
  computed: {
    highestEquippedArmour() {
      const equipped = Array.isArray(this.al?.equipped_armour)
        ? this.al.equipped_armour
        : []

      if (equipped.length === 0) {
        return null
      }

      const highestAp = Number(this.al?.highest_ap)
      if (!Number.isNaN(highestAp)) {
        const matchingAp = equipped.filter(
          (item) => Number(item?.ap) === highestAp,
        )
        if (matchingAp.length > 0) {
          return matchingAp.reduce((best, item) => {
            if (!best) return item
            const itemLayer = Number(item?.layer) || 0
            const bestLayer = Number(best?.layer) || 0
            if (itemLayer !== bestLayer)
              return itemLayer > bestLayer ? item : best
            const itemHp = Number(item?.hp) || 0
            const bestHp = Number(best?.hp) || 0
            return itemHp > bestHp ? item : best
          }, null)
        }
      }

      return equipped.reduce((best, item) => {
        if (!best) return item
        const itemAp = Number(item?.ap) || 0
        const bestAp = Number(best?.ap) || 0
        if (itemAp !== bestAp) return itemAp > bestAp ? item : best
        const itemLayer = Number(item?.layer) || 0
        const bestLayer = Number(best?.layer) || 0
        if (itemLayer !== bestLayer) return itemLayer > bestLayer ? item : best
        const itemHp = Number(item?.hp) || 0
        const bestHp = Number(best?.hp) || 0
        return itemHp > bestHp ? item : best
      }, null)
    },
    isUpperOrLower() {
      if (!this.al || !this.al.armour_location_name) return false
      const name = this.al.armour_location_name.toLowerCase()
      return name.startsWith('upper ') || name.startsWith('lower ')
    },
    isLeftRightBase() {
      if (!this.al || !this.al.armour_location_name) return false
      const name = this.al.armour_location_name.toLowerCase()
      const isUpperLower =
        name.startsWith('upper ') || name.startsWith('lower ')
      const isBaseLimb = name.startsWith('left ') || name.startsWith('right ')
      // base left/right (e.g. "Left Arm", "Right Leg") but not upper/lower variants
      return isBaseLimb && !isUpperLower
    },
    getHighestArmourName() {
      return this.highestEquippedArmour?.armour_name || 'None'
    },
    getHighestAPHP() {
      if (!this.highestEquippedArmour) {
        return '0 / 0'
      }

      return `${this.highestEquippedArmour.ap} / ${this.highestEquippedArmour.hp}`
    },
    equippedCount() {
      return Array.isArray(this.al.equipped_armour)
        ? this.al.equipped_armour.length
        : 0
    },
    hasMultipleEquipped() {
      return (
        Array.isArray(this.al.equipped_armour) &&
        this.al.equipped_armour.length > 1
      )
    },
    sortedEquipped() {
      const equipped = Array.isArray(this.al.equipped_armour)
        ? [...this.al.equipped_armour]
        : []
      // sort by descending layer
      return equipped.sort((a, b) => (b.layer || 0) - (a.layer || 0))
    },
    isEmpty() {
      const noEquipped =
        !Array.isArray(this.al.equipped_armour) ||
        this.al.equipped_armour.length === 0
      const noAp = !this.al.highest_ap || Number(this.al.highest_ap) === 0
      return noEquipped && noAp
    },
  },
  //========================================
  // methods
  //---------
  methods: {
    formatDexPen(value) {
      const numericValue = Number(value)
      if (!Number.isFinite(numericValue) || numericValue === 0) {
        return '0'
      }

      return numericValue.toFixed(2)
    },
    onMouseEnter() {
      if (this.hasMultipleEquipped) {
        this.showOverlay = true
      }
    },
    onMouseLeave() {
      this.showOverlay = false
    },
  },
}
</script>

<style scoped>
.btn-lg {
  font-size: 30px !important;
}

.btn.btn-primary[disabled] {
  background-color: #737373 !important;
  color: white !important;
  border-color: grey !important;
  opacity: 1 !important;
}

/* armour location name input text colour */
#location_name {
  color: #ffffff;
  font-size: 1.7rem;
  font-weight: 600;
}

/* body type location HP */
#location_hp {
  color: #ffffff;
  font-size: 1.7rem;
  font-weight: 600;
}

.equipped-count {
  color: orange;
}

/* smaller font size for input fields */
#armour_name,
#armour_aphp,
#armour_dex_pen {
  font-size: 1.2rem !important;
}

.armour-name-input {
  text-align: left !important;
}

label[for='armour_aphp'] {
  width: 100%;
  text-align: center !important;
}

/* highlight empty armour locations in orange */
.armour-empty #armour_name,
.armour-empty #armour_aphp,
.armour-empty #armour_dex_pen,
.armour-empty #location_name,
.armour-empty #location_hp {
  color: #25bfda;
}

/* tighten spacing between input fields on this card */
.armour-card .form-group {
  margin-bottom: 0.15rem;
}

/* ensure overlay positions relative to the whole card */
.armour-card {
  position: relative;
  overflow: hidden;
  width: 100%;
}

/* hover overlay with all equipped armour */
.armour-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: inherit;
}

.armour-overlay-inner {
  width: 100%;
  padding: 0.5rem;
}

.armour-overlay-header,
.armour-overlay-row {
  display: grid;
  /* widen Armour column, slightly shrink AP/HP and Dex Pen */
  grid-template-columns: 1fr 4fr 1.5fr 1.5fr;
  /* keep horizontal and vertical gaps visually consistent */
  column-gap: 0.2rem;
  font-size: 1.1rem;
  text-align: center;
  justify-items: center;
}

/* Armour column left-aligned */
.armour-overlay-header span:nth-child(2),
.armour-overlay-row span:nth-child(2) {
  text-align: left;
  justify-self: flex-start;
}

.armour-overlay-header {
  font-weight: 600;
  padding-bottom: 0.2rem;
  margin-bottom: 0.2rem;
}

.armour-overlay-row {
  padding: 0.2rem 0;
}

/* different grey for base left/right limb locations */
.limb-base-card {
  background-color: #494949 !important;
}

/* light grey background for upper/lower locations */
.upper-lower-card {
  background-color: #696868 !important;
}
</style>
